import { Router, type RouteLocationNormalized } from 'vue-router'
import { authService } from '../services/authService'

import { systemConfigService } from '../services/systemConfigService'

type ModuleEnabledMap = Record<string, boolean>

let moduleEnabledMap: ModuleEnabledMap = {}
let modulesLoaded = false
let modulesLoadPromise: Promise<void> | null = null

const routeNameToModuleCode: Record<string, string> = {
  // WIM
  'wim-dashboard': 'wim',
  wimdata: 'wim',
  'wim-speed': 'wim',
  'wim-reports': 'wim',
  wim: 'wim',

  // Watchdesk
  watchdesk: 'watchdesk',
  'watchdesk-dashboard': 'watchdesk',
  'watchdesk-reports': 'watchdesk',

  // Factory Weighing
  'factory-weighing': 'factory-weighing',
  'factory-dashboard': 'factory-weighing',
  'factory-weigh': 'factory-weighing',
  'factory-mobile-weighing': 'factory-weighing',
  'factory-records': 'factory-weighing',
  'kiosk-weigh': 'factory-weighing',
  'factory-reports': 'factory-weighing',

  // National Weighing
  'national-weighing': 'national-weighing',
  multideck: 'national-weighing',
  lswim: 'national-weighing',
  'mobile-weighing': 'national-weighing',
  'yard-management': 'national-weighing',
  'national-weighing-reports': 'national-weighing',

  // Vehicle Inspection
  'vehicle-inspection': 'vehicle-inspection',
  'inspection-dashboard': 'vehicle-inspection',
  'inspection-records': 'vehicle-inspection',
  'inspection-reports': 'vehicle-inspection',

  // Parking
  parking: 'parking',
  'parking-dashboard': 'parking',
  'parking-records': 'parking',
  'parking-reports': 'parking',

  // Tags
  tags: 'tags',
  'tags-dashboard': 'tags',
  'tags-records': 'tags',
  'tags-management': 'tags',
  'tags-reports': 'tags',

  // Traffic Census
  'traffic-census': 'traffic-census',
  'census-dashboard': 'traffic-census',
  'census-records': 'traffic-census',
  'census-reports': 'traffic-census',

  // Calibration
  calibration: 'calibration',
  'calibration-dashboard': 'calibration',
  'calibration-records': 'calibration',

  // Tolling
  tolling: 'tolling',
  'tolling-dashboard': 'tolling',
  'tolling-records': 'tolling',
  'tolling-rates': 'tolling',
  'tolling-booth': 'tolling',
  'tolling-reports': 'tolling',

  // Prosecution
  prosecution: 'prosecution',
  'prosecution-dashboard': 'prosecution',
  'prosecution-records': 'prosecution',
  'prosecution-reports': 'prosecution',
  'prosecution-settings': 'prosecution',
}

async function ensureModulesLoaded() {
  if (modulesLoaded) {
    return
  }

  if (!modulesLoadPromise) {
    modulesLoadPromise = (async () => {
      try {
        const modules = await systemConfigService.getModules()
        const map: ModuleEnabledMap = {}

        ;(modules || []).forEach((m: any) => {
          if (m?.code) {
            map[String(m.code).toLowerCase()] = !!m.isEnabled
          }
        })

        moduleEnabledMap = map
      } catch (error) {
        console.error('Failed to load modules configuration', error)
        moduleEnabledMap = {}
      } finally {
        modulesLoadPromise = null
        modulesLoaded = true
      }
    })()
  }

  await modulesLoadPromise
}

function getModuleCodeForRoute(to: RouteLocationNormalized): string | null {
  const routeName = typeof to.name === 'string' ? to.name : ''
  if (!routeName) {
    return null
  }
  return routeNameToModuleCode[routeName] ?? null
}

// Public routes that don't require authentication
// /factory/weigh and /factory/kiosk allowed for kiosk/terminal use; they use staticService.defaultLogin() for API auth
const publicRoutes = [
  '/auth/login',
  '/auth/signup',
  '/auth/recover-password',
  '/auth/recover-password-email',
  '/404',
  '/factory/weigh',
  '/factory/kiosk',
]

// Auth-only routes: when already logged in, redirect away to dashboard (don't show login again)
const authOnlyRoutes = ['/auth/login', '/auth/signup', '/auth/recover-password', '/auth/recover-password-email']

export function setupRouteGuards(router: Router) {
  router.beforeEach(async (to, from, next) => {
    const isPublicRoute = publicRoutes.includes(to.path)
    const isAuthOnlyRoute = authOnlyRoutes.includes(to.path)

    // Check if user is authenticated
    const isAuthenticated = authService.isAuthenticated()

    // If on login/signup etc. while already authenticated, redirect to dashboard (don't redirect from /factory/weigh)
    if (isAuthOnlyRoute && isAuthenticated) {
      next({ name: 'wim-dashboard' })
      return
    }

    // If trying to access a protected route while not authenticated, redirect to login
    if (!isPublicRoute && !isAuthenticated) {
      next({ name: 'login' })
      return
    }

    // If authenticated and accessing protected route, check module access and token
    if (!isPublicRoute && isAuthenticated) {
      try {
        // Check module enablement (based on system-config modules)
        const moduleCode = getModuleCodeForRoute(to)

        if (moduleCode) {
          await ensureModulesLoaded()

          const isEnabled = moduleEnabledMap[moduleCode.toLowerCase()]

          // If the module exists and is explicitly disabled, block access
          if (isEnabled === false) {
            next({ name: 'modules' })
            return
          }
        }

        // Check if token needs refresh (this will be handled by the auth service)
        next()
      } catch (error) {
        console.error('Authentication or module access check failed:', error)
        authService.logout('Authentication failed. Please log in again.')
        next({ name: 'login' })
      }
    } else {
      // Public route or not authenticated, proceed
      next()
    }
  })

  router.afterEach((to) => {
    // Update page title
    const title = (to.meta?.title as string) || 'mericrychlosti.cz'
    document.title = title
  })
}
